import type { ContactPayload, ContactResponse } from "./types";
import type { Dict, Locale } from "./i18n";
import {
  COUNTRIES,
  detectCountry,
  diagnosePhone,
  formatPhone,
  getCountry,
  placeholderFor,
  switchCountry,
  toE164,
  type Country,
} from "./phone";

type Status = "idle" | "loading" | "success" | "error";

interface FieldErrors {
  name?: string;
  phone?: string;
  email?: string;
  comment?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function interp(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? ""));
}

function emailReason(raw: string, v: Dict["validation"]): string | null {
  const s = raw.trim();
  if (!s.includes("@")) return v.emailMissingAt;
  const [, domain] = s.split("@");
  if (!domain || !domain.includes(".")) return v.emailMissingDomain;
  if (!EMAIL_RE.test(s)) return v.emailInvalid;
  return null;
}

function phoneReason(raw: string, country: Country, v: Dict["validation"]): string | null {
  const d = diagnosePhone(raw, country);
  if (!d) return null;
  const params = {
    dial: country.dial,
    country: country.flag + " " + country.dial,
    expected: d.expectedNational,
    got: d.gotNational,
  };
  return interp(v.phoneExpectDigits, params);
}

function validate(payload: ContactPayload, country: Country, v: Dict["validation"]): FieldErrors {
  const errors: FieldErrors = {};
  if (!payload.name.trim()) errors.name = v.required;
  else if (payload.name.trim().length < 2) errors.name = v.nameMin;

  if (!payload.phone.trim()) errors.phone = v.required;
  else {
    const reason = phoneReason(payload.phone, country, v);
    if (reason) errors.phone = reason;
  }

  if (!payload.email.trim()) errors.email = v.required;
  else {
    const reason = emailReason(payload.email, v);
    if (reason) errors.email = reason;
  }

  if (!payload.comment.trim()) errors.comment = v.required;
  else if (payload.comment.trim().length < 5) errors.comment = v.commentMin;

  return errors;
}

export function mountContactForm(root: HTMLElement, dict: Dict, locale: Locale) {
  let status: Status = "idle";
  let serverError: string | null = null;
  let serverSummary: string | null = null;
  let fieldErrors: FieldErrors = {};
  let country: Country = detectCountry();
  let values: ContactPayload = { name: "", phone: "", email: "", comment: "" };

  const f = dict.contacts.form;
  const v = dict.validation;

  const render = () => {
    const disabled = status === "loading";
    root.innerHTML = `
      <form class="form" novalidate>
        <div class="form__row">
          <label class="form__field">
            <span class="form__label">${escapeText(f.labels.name)}</span>
            <input class="form__input ${fieldErrors.name ? "form__input--error" : ""}" name="name" type="text" autocomplete="name" value="${escapeAttr(values.name)}" ${disabled ? "disabled" : ""} aria-invalid="${fieldErrors.name ? "true" : "false"}" />
            ${fieldErrors.name ? `<span class="form__error">${escapeText(fieldErrors.name)}</span>` : ""}
          </label>
          <label class="form__field">
            <span class="form__label">${escapeText(f.labels.phone)}</span>
            <div class="phone-input ${fieldErrors.phone ? "phone-input--error" : ""}">
              <select class="phone-input__country" name="country" ${disabled ? "disabled" : ""} aria-label="${escapeAttr(f.labels.phone)}">
                ${COUNTRIES.map(
                  (c) => `<option value="${escapeAttr(c.iso)}"${c.iso === country.iso ? " selected" : ""}>${c.flag} ${escapeText(c.dial)}</option>`,
                ).join("")}
              </select>
              <input class="phone-input__num" name="phone" type="tel" inputmode="tel" autocomplete="tel" placeholder="${escapeAttr(placeholderFor(country.mask))}" value="${escapeAttr(values.phone)}" ${disabled ? "disabled" : ""} aria-invalid="${fieldErrors.phone ? "true" : "false"}" />
            </div>
            ${fieldErrors.phone ? `<span class="form__error">${escapeText(fieldErrors.phone)}</span>` : ""}
          </label>
        </div>

        <label class="form__field">
          <span class="form__label">${escapeText(f.labels.email)}</span>
          <input class="form__input ${fieldErrors.email ? "form__input--error" : ""}" name="email" type="email" autocomplete="email" value="${escapeAttr(values.email)}" ${disabled ? "disabled" : ""} aria-invalid="${fieldErrors.email ? "true" : "false"}" />
          ${fieldErrors.email ? `<span class="form__error">${escapeText(fieldErrors.email)}</span>` : ""}
        </label>

        <label class="form__field">
          <span class="form__label">${escapeText(f.labels.comment)}</span>
          <textarea class="form__input form__input--textarea ${fieldErrors.comment ? "form__input--error" : ""}" name="comment" rows="5" ${disabled ? "disabled" : ""} aria-invalid="${fieldErrors.comment ? "true" : "false"}">${escapeText(values.comment)}</textarea>
          ${fieldErrors.comment ? `<span class="form__error">${escapeText(fieldErrors.comment)}</span>` : ""}
        </label>

        <button class="form__submit" type="submit" ${disabled ? "disabled" : ""}>
          ${escapeText(status === "loading" ? f.sending : f.send)}
        </button>

        ${status === "success" ? renderSuccess(serverSummary) : ""}
        ${status === "error" ? renderError(serverError) : ""}
      </form>
    `;

    const form = root.querySelector("form")!;
    form.addEventListener("submit", onSubmit);

    form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input[name], textarea[name]").forEach((el) => {
      if (el.name === "phone") {
        el.addEventListener("input", () => {
          values = { ...values, phone: formatPhone(el.value, country) };
          (el as HTMLInputElement).value = values.phone;
          if (fieldErrors.phone) {
            fieldErrors = { ...fieldErrors, phone: undefined };
            const wrap = el.closest(".phone-input");
            wrap?.classList.remove("phone-input--error");
            el.parentElement?.parentElement?.querySelector(".form__error")?.remove();
            el.setAttribute("aria-invalid", "false");
          }
        });
      } else {
        el.addEventListener("input", () => {
          const key = el.name as keyof ContactPayload;
          values = { ...values, [key]: el.value };
          if (fieldErrors[key]) {
            fieldErrors = { ...fieldErrors, [key]: undefined };
            render();
          }
        });
      }
    });

    const countrySel = form.querySelector<HTMLSelectElement>('select[name="country"]');
    countrySel?.addEventListener("change", () => {
      const next = getCountry(countrySel.value) || country;
      values = { ...values, phone: switchCountry(values.phone, country, next) };
      country = next;
      render();
    });
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    const errors = validate(values, country, v);
    if (Object.values(errors).some(Boolean)) {
      fieldErrors = errors;
      status = "idle";
      serverError = null;
      render();
      return;
    }

    fieldErrors = {};
    status = "loading";
    serverError = null;
    serverSummary = null;
    render();

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, phone: toE164(values.phone, country), locale }),
      });

      const data: ContactResponse = await res.json().catch(() => ({ ok: false, error: "Bad response" }));

      if (!res.ok || !data.ok) {
        status = "error";
        serverError = data.error || `Request failed (${res.status})`;
        render();
        return;
      }

      status = "success";
      serverSummary = data.summary ?? null;
      values = { name: "", phone: "", email: "", comment: "" };
      render();
    } catch (err) {
      status = "error";
      serverError = err instanceof Error ? err.message : f.errorFallback;
      render();
    }
  };

  function renderSuccess(summary: string | null): string {
    return `
      <div class="form__state form__state--success" role="status">
        <strong>${escapeText(f.successTitle)}</strong> ${escapeText(f.successBody)}
        ${summary ? `<p class="form__state-sub">${escapeText(f.summaryPrefix)} <em>${escapeText(summary)}</em></p>` : ""}
      </div>
    `;
  }

  function renderError(msg: string | null): string {
    return `
      <div class="form__state form__state--error" role="alert">
        <strong>${escapeText(f.errorTitle)}</strong> ${escapeText(msg || f.errorFallback)}
      </div>
    `;
  }

  render();
}

function escapeAttr(s: string): string {
  return s.replace(/[&"<>]/g, (c) => ({ "&": "&amp;", '"': "&quot;", "<": "&lt;", ">": "&gt;" })[c]!);
}
function escapeText(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!);
}
